import React, { useState, useEffect, useMemo } from 'react';
import { format, addDays, parseISO } from 'date-fns';
import { toast } from 'react-toastify';
import {
  FiCalendar,
  FiClock,
  FiLayers,
  FiRefreshCw,
  FiTrash2,
  FiCheckCircle,
  FiPlusCircle
} from 'react-icons/fi';
import api from '../config/api';
import LoadingSpinner from '../components/LoadingSpinner';
import AdminNav from '../components/AdminNav';

const dayOptions = [
  { key: 'sunday', label: 'Sun', value: 0 },
  { key: 'monday', label: 'Mon', value: 1 },
  { key: 'tuesday', label: 'Tue', value: 2 },
  { key: 'wednesday', label: 'Wed', value: 3 },
  { key: 'thursday', label: 'Thu', value: 4 },
  { key: 'friday', label: 'Fri', value: 5 },
  { key: 'saturday', label: 'Sat', value: 6 }
];

const today = new Date();
const defaultRange = {
  startDate: format(today, 'yyyy-MM-dd'),
  endDate: format(addDays(today, 14), 'yyyy-MM-dd')
};

const AdminAvailability = () => {
  const [range, setRange] = useState(defaultRange);
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [singleForm, setSingleForm] = useState({ date: '', startTime: '', endTime: '' });
  const [recurringForm, setRecurringForm] = useState({
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    daysOfWeek: []
  });
  const [creatingSingle, setCreatingSingle] = useState(false);
  const [creatingRecurring, setCreatingRecurring] = useState(false);

  useEffect(() => {
    fetchAvailability();
  }, [range]);

  const fetchAvailability = async () => {
    setLoading(true);
    try {
      const response = await api.get('/availability/admin', {
        params: {
          startDate: range.startDate,
          endDate: range.endDate
        }
      });
      setAvailability(response.data);
    } catch (error) {
      toast.error('Failed to load availability');
    } finally {
      setLoading(false);
    }
  };

  const groupedSlots = useMemo(() => {
    const groups = availability.reduce((acc, slot) => {
      if (!acc[slot.date]) acc[slot.date] = [];
      acc[slot.date].push(slot);
      return acc;
    }, {});

    return Object.entries(groups)
      .sort(([dateA], [dateB]) => new Date(dateA) - new Date(dateB))
      .map(([date, slots]) => ({
        date,
        slots: slots.sort((a, b) => a.startTime.localeCompare(b.startTime))
      }));
  }, [availability]);

  const stats = useMemo(() => {
    const total = availability.length;
    const booked = availability.filter((slot) => slot.isBooked).length;
    return {
      total,
      available: total - booked,
      booked
    };
  }, [availability]);

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    if (!singleForm.date || !singleForm.startTime || !singleForm.endTime) {
      toast.error('Please fill all fields');
      return;
    }

    setCreatingSingle(true);
    try {
      await api.post('/availability', singleForm);
      toast.success('Availability slot created');
      setSingleForm({ date: '', startTime: '', endTime: '' });
      fetchAvailability();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create slot');
    } finally {
      setCreatingSingle(false);
    }
  };

  const toggleRecurringDay = (dayKey) => {
    setRecurringForm((prev) => {
      const exists = prev.daysOfWeek.includes(dayKey);
      if (exists) {
        return { ...prev, daysOfWeek: prev.daysOfWeek.filter((d) => d !== dayKey) };
      }
      return { ...prev, daysOfWeek: [...prev.daysOfWeek, dayKey] };
    });
  };

  const handleRecurringSubmit = async (e) => {
    e.preventDefault();
    const { startDate, endDate, startTime, endTime, daysOfWeek } = recurringForm;

    if (!startDate || !endDate || !startTime || !endTime || daysOfWeek.length === 0) {
      toast.error('Select date range, time, and at least one weekday');
      return;
    }

    if (new Date(endDate) < new Date(startDate)) {
      toast.error('End date must be after start date');
      return;
    }

    const dayNumbers = daysOfWeek.map(
      (dayKey) => dayOptions.find((d) => d.key === dayKey)?.value
    );

    const slots = [];
    let cursor = new Date(startDate);
    const end = new Date(endDate);
    while (cursor <= end) {
      if (dayNumbers.includes(cursor.getDay())) {
        slots.push({
          date: format(cursor, 'yyyy-MM-dd'),
          startTime,
          endTime
        });
      }
      cursor = addDays(cursor, 1);
    }

    if (slots.length === 0) {
      toast.error('No matching days found within the selected range');
      return;
    }

    setCreatingRecurring(true);
    try {
      await api.post('/availability/bulk', { slots });
      toast.success(`${slots.length} slots scheduled`);
      setRecurringForm({
        startDate: '',
        endDate: '',
        startTime: '',
        endTime: '',
        daysOfWeek: []
      });
      fetchAvailability();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create slots');
    } finally {
      setCreatingRecurring(false);
    }
  };

  const handleDeleteSlot = async (slot) => {
    if (slot.isBooked) {
      toast.error('Cannot delete a booked slot');
      return;
    }
    if (!window.confirm('Delete this availability slot?')) return;

    try {
      await api.delete(`/availability/${slot.id}`);
      toast.success('Slot deleted');
      fetchAvailability();
    } catch (error) {
      toast.error('Failed to delete slot');
    }
  };

  const setQuickRange = (days) => {
    const newStart = new Date();
    const newEnd = addDays(new Date(), days - 1);
    setRange({
      startDate: format(newStart, 'yyyy-MM-dd'),
      endDate: format(newEnd, 'yyyy-MM-dd')
    });
  };

  if (loading) return <LoadingSpinner fullScreen />;

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Availability Planner</h1>
            <p className="text-gray-600 mt-2">
              Build weekly schedules and keep your calendar updated in real-time
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setQuickRange(7)}
              className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:border-primary-500"
            >
              Next 7 days
            </button>
            <button
              onClick={() => setQuickRange(14)}
              className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:border-primary-500"
            >
              14 days
            </button>
            <button
              onClick={() => setQuickRange(30)}
              className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:border-primary-500"
            >
              30 days
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-6 shadow-sm flex items-center space-x-4">
            <div className="w-12 h-12 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center">
              <FiCalendar size={22} />
            </div>
            <div>
              <p className="text-sm text-gray-500 uppercase tracking-wide">Total slots</p>
              <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm flex items-center space-x-4">
            <div className="w-12 h-12 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
              <FiCheckCircle size={22} />
            </div>
            <div>
              <p className="text-sm text-gray-500 uppercase tracking-wide">Available</p>
              <p className="text-3xl font-bold text-green-600">{stats.available}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm flex items-center space-x-4">
            <div className="w-12 h-12 rounded-full bg-red-100 text-red-500 flex items-center justify-center">
              <FiLayers size={22} />
            </div>
            <div>
              <p className="text-sm text-gray-500 uppercase tracking-wide">Booked</p>
              <p className="text-3xl font-bold text-red-500">{stats.booked}</p>
            </div>
          </div>
        </div>

        {/* Range Picker */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
              <input
                type="date"
                value={range.startDate}
                onChange={(e) => setRange({ ...range, startDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
              <input
                type="date"
                value={range.endDate}
                min={range.startDate}
                onChange={(e) => setRange({ ...range, endDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={fetchAvailability}
                className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg font-semibold flex items-center justify-center space-x-2"
              >
                <FiRefreshCw />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>

        {/* Forms */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center space-x-2 mb-4">
              <FiPlusCircle className="text-primary-500" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Add Single Slot</h2>
                <p className="text-sm text-gray-500">Perfect for ad-hoc availability</p>
              </div>
            </div>
            <form onSubmit={handleSingleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                <input
                  type="date"
                  value={singleForm.date}
                  min={format(today, 'yyyy-MM-dd')}
                  onChange={(e) => setSingleForm({ ...singleForm, date: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                  <input
                    type="time"
                    value={singleForm.startTime}
                    onChange={(e) => setSingleForm({ ...singleForm, startTime: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                  <input
                    type="time"
                    value={singleForm.endTime}
                    onChange={(e) => setSingleForm({ ...singleForm, endTime: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={creatingSingle}
                className="w-full bg-gradient-to-r from-primary-500 to-secondary-500 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50"
              >
                {creatingSingle ? 'Adding...' : 'Add Slot'}
              </button>
            </form>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center space-x-2 mb-4">
              <FiLayers className="text-primary-500" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Schedule Recurring Slots</h2>
                <p className="text-sm text-gray-500">Plan weekly availability in bulk</p>
              </div>
            </div>
            <form onSubmit={handleRecurringSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={recurringForm.startDate}
                    min={format(today, 'yyyy-MM-dd')}
                    onChange={(e) => setRecurringForm({ ...recurringForm, startDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={recurringForm.endDate}
                    min={recurringForm.startDate || format(today, 'yyyy-MM-dd')}
                    onChange={(e) => setRecurringForm({ ...recurringForm, endDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                  <input
                    type="time"
                    value={recurringForm.startTime}
                    onChange={(e) => setRecurringForm({ ...recurringForm, startTime: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                  <input
                    type="time"
                    value={recurringForm.endTime}
                    onChange={(e) => setRecurringForm({ ...recurringForm, endTime: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Weekdays</label>
                <div className="grid grid-cols-7 gap-2">
                  {dayOptions.map((day) => (
                    <button
                      key={day.key}
                      type="button"
                      onClick={() => toggleRecurringDay(day.key)}
                      className={`py-2 rounded-lg text-sm font-semibold border ${
                        recurringForm.daysOfWeek.includes(day.key)
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'border-gray-200 text-gray-600 hover:border-primary-300'
                      }`}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>
              <button
                type="submit"
                disabled={creatingRecurring}
                className="w-full bg-gray-900 text-white py-3 rounded-lg font-semibold hover:bg-gray-800 transition disabled:opacity-50"
              >
                {creatingRecurring ? 'Scheduling...' : 'Schedule Recurring Slots'}
              </button>
            </form>
          </div>
        </div>

        {/* Availability List */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">Upcoming Availability</h2>
              <p className="text-sm text-gray-500">
                Showing {groupedSlots.length} days between {range.startDate} and {range.endDate}
              </p>
            </div>
          </div>

          {groupedSlots.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              No availability scheduled for this range.
            </div>
          ) : (
            <div className="space-y-6">
              {groupedSlots.map((group) => (
                <div key={group.date} className="border border-gray-100 rounded-xl">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">
                        {format(parseISO(group.date), 'EEEE, MMMM d, yyyy')}
                      </p>
                      <p className="text-xs text-gray-400 uppercase tracking-wide">
                        {group.slots.length} slot{group.slots.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <span className="text-xs font-semibold px-3 py-1 rounded-full bg-primary-100 text-primary-600">
                      {group.slots.filter((slot) => !slot.isBooked).length} open
                    </span>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {group.slots.map((slot) => (
                      <div key={slot.id} className="px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2 text-gray-800 font-semibold">
                            <FiClock className="text-primary-500" />
                            <span>
                              {slot.startTime.slice(0, 5)} - {slot.endTime.slice(0, 5)}
                            </span>
                          </div>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              slot.isBooked ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'
                            }`}
                          >
                            {slot.isBooked ? 'Booked' : 'Available'}
                          </span>
                        </div>
                        <div>
                          {!slot.isBooked && (
                            <button
                              onClick={() => handleDeleteSlot(slot)}
                              className="text-red-500 hover:text-red-600"
                            >
                              <FiTrash2 size={18} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAvailability;

